import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal, Pencil, Trash2, Send, AlertTriangle, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import ColumnRow from './ColumnRow';
import { MANUAL_TASKS_KIND } from './taskConstants';

/** One saved query on the board: live count, its matches, and the actions that apply to it. */
export default function ColumnCard({
  column, actions, onEdit, onDelete, onSendMessage, onShowAll, onMove, onAddTask, isFirst, isLast,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const hasMore = column.count > column.rows.length;

  return (
    <div className="kanban-col col-card">
      <div className="kanban-col-head">
        <div style={{ minWidth: 0 }}>
          <h4 title={column.ruleSummary || undefined}>{column.name}</h4>
          {column.ruleSummary && <div className="col-rule">{column.ruleSummary}</div>}
        </div>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <span className="k-count">{column.error ? '!' : column.count}</span>
          {/* Manual tasks are the one kind whose rows are authored rather than derived, so this is
              the one column that offers an add button. */}
          {column.ruleKind === MANUAL_TASKS_KIND && (
            <button className="btn-icon-sm" title="Add a task" onClick={() => onAddTask?.(column)}>
              <Plus size={15} />
            </button>
          )}
          <span className="dropdown" ref={menuRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <button className="btn-icon-sm" title="Column options" onClick={() => setMenuOpen((v) => !v)}>
              <MoreHorizontal size={15} />
            </button>
            {menuOpen && (
              <div className="dd-menu" style={{ minWidth: 190, padding: 6 }}>
                <button className="dd-item" style={ITEM}
                  onClick={() => { setMenuOpen(false); onSendMessage(column); }}
                  disabled={column.count === 0}>
                  <Send size={13} /> Send message…
                </button>
                <button className="dd-item" style={ITEM} onClick={() => { setMenuOpen(false); onEdit(column); }}>
                  <Pencil size={13} /> Edit column
                </button>
                <button className="dd-item" style={ITEM} disabled={isFirst}
                  onClick={() => { setMenuOpen(false); onMove(column, -1); }}>
                  <ChevronLeft size={13} /> Move left
                </button>
                <button className="dd-item" style={ITEM} disabled={isLast}
                  onClick={() => { setMenuOpen(false); onMove(column, 1); }}>
                  <ChevronRight size={13} /> Move right
                </button>
                <button className="dd-item" style={{ ...ITEM, color: 'var(--red-500)' }}
                  onClick={() => { setMenuOpen(false); onDelete(column); }}>
                  <Trash2 size={13} /> Delete column
                </button>
              </div>
            )}
          </span>
        </span>
      </div>

      <div className="kanban-cards">
        {column.error && (
          <div className="col-empty" style={{ color: 'var(--red-500)' }}>
            <AlertTriangle size={15} /> {column.error}
          </div>
        )}

        {!column.error && column.rows.map((row) => (
          <ColumnRow key={row.matchToken} row={row} columnId={column.id} actions={actions} />
        ))}

        {!column.error && column.rows.length === 0 && (
          <div className="col-empty">Nobody matches on this date.</div>
        )}

        {hasMore && (
          <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => onShowAll(column)}>
            Show all {column.count}
          </button>
        )}

        {column.truncated && (
          <div className="col-empty" style={{ fontSize: '0.72rem' }}>
            Showing the first {column.count} — narrow this column to see everything.
          </div>
        )}
      </div>
    </div>
  );
}

const ITEM = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  width: '100%',
  textAlign: 'left',
  padding: '7px 8px',
  fontSize: '0.82rem',
  borderRadius: 'var(--r-sm)',
};
